import {
    ScheduleDeleteJournalEntryMode,
    ScheduleLifecycleVerb,
} from "@common/types/domains/accounting/schedule";
import { ScheduleStopSettlement } from "@common/schemas/domains/accounting/scheduleLifecycle.schema";
import {
    SCHEDULE_DELETE_BULK_FAILED_MESSAGE,
    SCHEDULE_LIFECYCLE_BULK_FAILED_MESSAGE,
    useDeleteScheduleApplicationsBulkMutation,
    useScheduleLifecycleBulkMutation,
} from "@api/scheduleLifecycleBulk.api";
import { invalidateAfterLifecycleAction } from "@api/scheduleTemplate.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useScope } from "@state/scopeContext.lib";
import { runBulkRequestGroups } from "./bulkRequestRunner";
import {
    formatScheduleBulkLifecycleFailureToast,
    formatScheduleBulkLifecycleToast,
} from "./scheduleLifecycleBulkCopy";
import { ScheduleBulkEntityGroup } from "./scheduleLifecycleBulkHelpers";

export type ScheduleLifecycleBulkAction =
    | { verb: ScheduleLifecycleVerb.pause }
    | { verb: ScheduleLifecycleVerb.resume; effectiveDate: string }
    | {
          verb: ScheduleLifecycleVerb.cancel;
          settlement?: ScheduleStopSettlement;
      }
    | {
          verb: ScheduleLifecycleVerb.delete;
          journalEntryMode: ScheduleDeleteJournalEntryMode;
      };

export type ScheduleLifecycleBulkSubmission = ScheduleLifecycleBulkAction & {
    groups: ScheduleBulkEntityGroup[];
    skippedApplicationIds?: string[];
};

interface GroupResult {
    updatedCount: number;
    failed: Array<{ applicationId: string; message: string }>;
}

export function useScheduleLifecycleBulkSubmit(options: {
    onClose: () => void;
    onCompleted: (failedApplicationIds: string[]) => void;
}): {
    submit: (submission: ScheduleLifecycleBulkSubmission) => Promise<void>;
    isPending: boolean;
} {
    const { onClose, onCompleted } = options;
    const { workspaceId } = useScope();
    const { toast, error } = useToaster();
    const lifecycleMutation = useScheduleLifecycleBulkMutation();
    const deleteMutation = useDeleteScheduleApplicationsBulkMutation();

    const runGroup = async (
        submission: ScheduleLifecycleBulkSubmission,
        group: ScheduleBulkEntityGroup,
    ): Promise<GroupResult> => {
        if (submission.verb === ScheduleLifecycleVerb.delete) {
            const result = await deleteMutation.mutateAsync({
                entityId: group.entityId,
                applicationIds: group.applicationIds,
                journalEntryMode: submission.journalEntryMode,
            });
            return {
                updatedCount: result.removed.length,
                failed: result.failed,
            };
        }
        const result = await lifecycleMutation.mutateAsync({
            entityId: group.entityId,
            verb: submission.verb,
            applicationIds: group.applicationIds,
            effectiveDate:
                submission.verb === ScheduleLifecycleVerb.resume
                    ? submission.effectiveDate
                    : undefined,
            settlement:
                submission.verb === ScheduleLifecycleVerb.cancel
                    ? submission.settlement
                    : undefined,
        });
        return { updatedCount: result.updated.length, failed: result.failed };
    };

    const submit = async (submission: ScheduleLifecycleBulkSubmission) => {
        const fallbackMessage =
            submission.verb === ScheduleLifecycleVerb.delete
                ? SCHEDULE_DELETE_BULK_FAILED_MESSAGE
                : SCHEDULE_LIFECYCLE_BULK_FAILED_MESSAGE;
        const outcome = await runBulkRequestGroups({
            groups: submission.groups,
            run: (group) => runGroup(submission, group),
            countRows: (group) => group.applicationIds.length,
            fallbackMessage,
        });
        invalidateAfterLifecycleAction(workspaceId);
        const updatedCount = outcome.results.reduce(
            (sum, result) => sum + result.updatedCount,
            0,
        );
        if (updatedCount === 0) {
            const [firstFailureMessage] = outcome.failedMessages;
            error(
                formatScheduleBulkLifecycleFailureToast({
                    verb: submission.verb,
                    firstFailureMessage:
                        firstFailureMessage === fallbackMessage
                            ? undefined
                            : firstFailureMessage,
                }),
            );
            return;
        }
        toast(
            formatScheduleBulkLifecycleToast({
                verb: submission.verb,
                updatedCount,
                failedCount: outcome.failedMessages.length,
            }),
        );
        onCompleted([
            ...outcome.results.flatMap((result) =>
                result.failed.map((row) => row.applicationId),
            ),
            ...outcome.thrownGroups.flatMap((group) => group.applicationIds),
            ...(submission.skippedApplicationIds ?? []),
        ]);
        onClose();
    };

    return {
        submit,
        isPending: lifecycleMutation.isPending || deleteMutation.isPending,
    };
}
